import React, { useMemo, useState } from "react";
import AccordionPart from "../components/shared/AccordionPart";
import Modal from "../components/shared/Modal";
import PROJECT_CONFIG from "../config/projectConfig";

const RulesProcedures = () => {
  const parts = useMemo(
    () => [
      {
        id: "part-i",
        title: "PART I - BACKGROUND",
        sections: [
          {
            id: "1.1",
            title: "1.1 Introduction",
            content: `As provided for in the National Flood Insurance Act (the Act) and under contract with FEMA, the National Institute of Building Sciences (NIBS) administer Scientific Resolution Panels ("SRP" or "Panel") for the purpose of performing independent reviews of scientific and technical data used by FEMA to develop flood elevations for the National Flood Insurance Program's (NFIP) Flood Insurance Rate Maps (FIRM). The objective of the SRP process is to assist FEMA and communities (the Parties) in efficiently and impartially review and resolve conflicting data presented to FEMA. SRPs are established to achieve the following benefits to both FEMA and communities:

- Offer a process deemed neutral for all parties.
- Offer the best independent scientific and technical expertise available to review and decide on conflicting flood hazard data for flood maps.
- Provide efficient resolution to community challenges of proposed flood elevations.

An SRP is not intended to be the first step toward resolution. Historically, FEMA has used community consultation to resolve appeals and comments to proposed flood elevations. FEMA continues to be committed to the concept of community consultation to resolve conflicting data in an interactive, collaborative process which typically leads to satisfactory resolution. On occasions when community consultation cannot produce a mutually acceptable resolution, the SRP will be made available.`,
          },
          {
            id: "1.2",
            title: "1.2 Purpose",
            content:
              "The SRP process has been established for the purpose of performing independent reviews of scientific and technical data submitted by a community challenging FEMA's flood elevation data and FIRMs. Based on the scientific and technical data submitted by the community to FEMA during the appeal period and issued by FEMA at preliminary map issuance, the Panel will render a written decision as to which data and analyses represent the best available science. The Panel's decision will serve as the recommendation to the FEMA Administrator for FEMA's final determination of the flood elevations.",
          },
          {
            id: "1.3",
            title: "1.3 Eligibility",
            content: `To be considered an eligible request for an SRP process, an appeal or comment must:

- Be received during the 90-day appeal period;
- Include knowledge or information indicating that the elevations proposed by FEMA are scientifically or technically incorrect and/or demonstrate that the application of the community's technical and scientific data result in more correct estimates of flood elevations;
- Include certifications of the supporting data by a registered professional engineer or licensed land surveyor;
- Not have been wholly accepted to be incorporated by FEMA into a revised preliminary flood map;
- Have been subject to a minimum of 60 days good-faith consultation between FEMA and the community; and
- Have the community CEO or authorized representative complete and submit, to FEMA during the stipulated timeframe, an SRP Request Form (Appendix B) which includes the community's specific contentions of the incorrect scientific and technical information used by FEMA in developing the proposed flood elevations among other information and an SRP Community Submittal Agreement (Appendix C).`,
          },
          {
            id: "1.4",
            title: "1.4 Request",
            content: `An SRP will be convened at the request of FEMA or a community upon submission of an SRP Request Form and an SRP Community Submittal Agreement to the FEMA Regional Office. The SRP Request Form will include, among other information:

- The community's specific contentions of the incorrect scientific and technical data used by FEMA in developing the proposed flood elevations; and
- A signed Community Submittal Agreement confirming the community's commitment to release of all liability the Panelists for their participation on the SRP and NIBS for administering the SRP process.`,
          },
        ],
      },
      {
        id: "part-ii",
        title: "PART II - ORGANIZATION",
        sections: [
          {
            id: "2.1",
            title: "2.1 Agreement of Parties",
            content:
              "The Parties agree to these rules and procedures, and any amendments in effect, at the time the administrative requirements for an SRP request have been met and an independent Scientific Resolution Panel has been convened to hear and decide on a community's challenge of FEMA's proposed flood elevations.",
          },
          {
            id: "2.2",
            title: "2.2 Establishment of Authority",
            content: `NIBS, pursuant to a contract with FEMA, as Panel Sponsor has established and maintains a Cadre of scientific experts (hereinafter referred to as "SRP Cadre members") from which will be convened an independent scientific body (referred to as an "SRP" or "Panel"); to make the SRP available to FEMA and communities pursuant to the Act; and to administer SRP operations. The purpose of these rules is to set forth the understanding, terms and conditions by which Panelist will become members of the SRP Cadre of experts and, if selected, serve on an individual Panel, and by which FEMA and the community submit their conflicting data to an SRP for resolution.

NIBS may terminate a Panel at any time for convenience or cause.`,
          },
          {
            id: "2.3",
            title: "2.3 Amendments",
            content:
              "Amendments to these rules will require approval of FEMA and NIBS CEO. Amendments to these rules approved by FEMA and NIBS CEO will become effective on a date to be determined by staff but no later than a request to convene a new SRP.",
          },
          {
            id: "2.4",
            title: "2.4 Scope and Limit of Responsibility",
            content: `The Panel, once appointed, will convene to review the conflicting data submitted by FEMA and the community, hear oral presentations from FEMA and the community if deemed necessary and establish a majority decision on the appropriate data. The Panel will then render a written recommendation, with supporting rationale, as to whether FEMA's proposed flood elevations are scientifically or technically incorrect, as provided for in the National Flood Act, as amended by (42 USC 4104) (the Act), the Federal Emergency Management regulations (44 CFR Part 67) (the regulations) and in accordance with these rules. The Panel's decision will constitute a recommendation to the FEMA Administrator, who will render a final determination.

Based on the scientific and technical information used by FEMA to generate the flood maps and the data submitted by the community, the Panel will:

- First, review FEMA data for sound engineering practice and principles, limited to the contested data.
- Second, review community data and determine if:
  - it satisfies NFIP mapping standards, and
  - it is superior to FEMA data.
- Establish its decision based on these reviews and recommend either the acceptance or denial of the community submitted data for inclusion in a revised flood map in part or in whole.

Based on its review of the scientific and technical information submitted by the community and FEMA, the Panel will make only one of the following decisions on a point by point basis:

- FEMA's data does not satisfy NFIP mapping standards defined in FEMA Guidelines and Standards for Flood Risk Analysis and Mapping and supporting guidance or FEMA Guidelines and Specifications for Flood Hazard Mapping Partners (for studies begun before the superseded date) (NFIP standards) and must be revisited.
- Community's data satisfies NFIP standards and wholly corrects or negates FEMA's data.
- Portions of the Community's data satisfy NFIP standards and correct or negate FEMA's data.
- Community's data does not satisfy NFIP standards, thus FEMA's data is not corrected, contradicted, or negated.
- Community's data satisfies NFIP standards and is correct, but does not negate FEMA's data.`,
          },
          {
            id: "2.5",
            title: "2.5 Initiation of SRP",
            content: `When FEMA determines to make the SRP process available for a specific challenge, and the community(s) agrees, FEMA will notify NIBS to convene a Panel by submitting an SRP Request Form, executed by the community(s) CEO, containing the name of the community(s), whether the appeal is from a single community or joint-communities, consisting of no more than two (2) communities, the nature of the challenge, the scientific and technical data needed to develop a list of potential Panelists, the name, email address and phone number of the community(s) CEO or authorized representative, a signed SRP Community Submittal Agreement(s) executed by the community(s) CEO, appropriate sections of the Technical and Scientific Data Notebook (TSDN) related to the conflicting flood elevations and a summary of the issue(s), as well as the name, email address and phone number of the parties involved in the challenge, including FEMA's Flood Mapping Partner.

FEMA may choose to exercise an SRP process without community endorsement for other scientific needs as deemed appropriate by the FEMA Administrator or Federal Insurance and Mitigation Administrator in consultation with the Office of Chief Counsel.

NIBS will confirm receipt of an SRP request to FEMA and the community within Two (2) weeks of receipt of an SRP Request Form.`,
          },
          {
            id: "2.6",
            title: "2.6 Administrative Review of SRP Request",
            content:
              "NIBS may initiate an administrative review of the SRP Request, by telephone, with FEMA and the community to address such issues as Panel member selection, review the nature of the challenge and the technical and scientific data submitted, expertise needed on the Panel or any other administrative matters.",
          },
          {
            id: "2.7",
            title: "2.7 Scientific Resolution Panel Cadre Membership",
            content: `Membership to the SRP Cadre is open to individuals with proven scientific and technical knowledge and expertise in the field of surface water hydrology, hydraulics, coastal engineering, or other engineering and scientific fields relating to the creation of Flood Hazard Maps and Flood Insurance Studies. Persons meeting and exceeding the SRP qualification criteria and interested in serving on a Scientific Resolution Panel will complete an online application form; provide all required supporting documentation requested on the form, and complete an affidavit certifying knowledge and understanding of the Act, the Regulations, the SRP Rules and Procedures and any other required reading.

Confirmation or rejection of membership will be issued by NIBS to the applicant within 15 days of receipt of the fully completed online application form.

Cadre members will agree to update their professional information on an annual basis, or as necessary, for the duration of their membership on the SRP Cadre.

When selected to serve on a Panel, Cadre members will be hired by NIBS and considered an independent contractor to NIBS.`,
          },
          {
            id: "2.8",
            title: "2.8 Availability and Term of Service",
            content: `Cadre members will agree to serve on at least one Panel per year, if so requested, averaging two to five 8-hour-days effort over the course of 90 days.

Cadre members may withdraw from the SRP Cadre at any time with 10 days written notice to NIBS prior to the effective date of the withdrawal. Cadre members will not withdraw from the SRP during service on a Panel except for good cause given in writing to NIBS.`,
          },
          {
            id: "2.9",
            title: "2.9 Panel Members Selection",
            content: `Within 60 business days after receipt of an SRP Request Form, NIBS will send to FEMA and the community(s) a short list of eight (8) pre-qualified potential Panelists chosen from the SRP Cadre of experts. Selection of potential panelists will be based on the technical challenge of the appeal or comment and the expertise required to review FEMA's and the community's conflicting data. All potential pre-qualified short-listed Cadre members will confirm that they are available to serve, have no conflict of interest with the parties to the appeal or FEMA's flood mapping partner(s) and do not reside in the community's state.

Panelists with expertise in the following areas will be selected from NIBS SRP Cadre of experts, and chosen based on the technical challenges of each appeal or comment:

- 1D and 2D floodway/floodplain analysis
- Mapping floodways and profiles for Riverine and Coastal Transition Zone flood prone areas
- Coastal flood hazard analysis
- Riverine flood hazard analysis, ranging from simple calculations to use of any of the models on FEMA's acceptable models list including 2-dimensional modeling
- Lacustrine flood hazard analysis, ranging from simple calculations to use of any of the models on FEMA's acceptable models list
- Alluvial fan flood hazards analysis, ranging from simple calculations to use of any of the models on FEMA's acceptable models list
- Water resource engineering
  - Surface water hydrology
  - Open-channel flow hydraulics
  - Hydrologic engineering
- Coastal storm and hydraulic engineering
- Coastal geotechnical commerce
- Coastal storm generation
- Geotechnical engineering
- Topographic data developed from both survey and remote sensing methods, including Light Detection and Ranging (LiDAR) techniques
- FEMA Guidelines and Standards for Flood Risk Analysis and Mapping and supporting guidance
- Superseded FEMA Guidelines and Specifications for Flood Hazard Mapping Partners (May apply to studies begun before the superseded date)

FEMA and the community(s) will select a Panel of 5 members, from the submitted short list of potential Panelists within 30 calendar days from the transmittal date of the list of potential Panelists. A single community will designate a simple majority (3) of the Panel members and FEMA will designate the balance of the Panel members (2). When a joint panel is requested, each community will designate two (2) Panel members each and FEMA will designate the fifth panel member.

If FEMA and/or community(s) fail to designate any or some of the Panel members within 30 days from the transmittal date of the online list of potential Panelists, NIBS will select the remaining members to complete the five-member Panel.

FEMA and the community(s) will have 15 calendar days from the transmittal date of the short list of potential panelists to indicate name(s) objected to and return the list to NIBS. If a party does not return the list within the time specified, all potential Panelists on the list will be deemed acceptable.

Upon receipt of the name or names objected to by FEMA or the Community, a new selection process will be initiated to prequalify new potential panelist(s).

If for any reason, a partial or full appointment cannot be made from the FEMA or community designated selection, an updated or new list will be sent to FEMA and community and a new selection process will commence.`,
          },
          {
            id: "2.10",
            title: "2.10 Vacancies",
            content:
              "If, for any reason, a Panelist is unable to perform the duties of a Panel, NIBS will declare the applicable party selection vacant. The Institute will provide a short-list of two (2) potential Panelists from which FEMA or the community will choose a new member in accordance with these applicable rules.",
          },
          {
            id: "2.11",
            title: "2.11 Chair Selection",
            content:
              "From the lists of FEMA and community selected Panelists NIBS will invite a member to Chair the Panel. If members do not respond to NIBS invitation, NIBS will make the appointment.",
          },
          {
            id: "2.12",
            title: "2.12 Scientific Resolution Panel",
            content: `Each Panel will consist of 5 Panelists based on the availability of Panelists and or scope of the appeal or comment.

Pre-qualified Panelists will be representatives from federal agencies, academia and private industry. FEMA, DHS and federal employees that are a party to a study will not be able to serve on a Panel. Pre-qualified Panelists may not reside in the state from which the challenge has been filed.

Panelist will be considered an independent contractor to NIBS. NIBS will not direct or control Panelist's work on any Panel.

Panel members may not serve on more than one Panel at a time.`,
          },
          {
            id: "2.13",
            title: "2.13 Disclosure - Conflict of Interest",
            content: `SRP Cadre experts will disclose on their application form any circumstances likely to give rise to doubt as to their impartiality or independence, including any bias or any financial or personal interest in the result of an SRP decision.

Upon notification that one has been short-listed to serve on a Panel, Panelist will disclose to NIBS any actual conflict of interest with the parties to the appeal or comment, or any circumstances that might be perceived as constituting a conflict of interest or a bias in favor of or against either party. Short-listed and appointed Panelists must disclose any facts or circumstances that might be seen as affecting their ability to decide the appeal or comment fairly and impartially. In the event of such a disclosure, NIBS will determine whether a different Panelist should be selected to serve on the panel.`,
          },
          {
            id: "2.14",
            title: "2.14 Panel Member Removal",
            content:
              "NIBS may remove Panelist from a Panel, at its sole discretion, for convenience or cause, including for apparent or actual conflict of interest, demonstrated bias or partiality, misconduct related to service on the Panel, or misrepresentation on Panelist's application profile.",
          },
          {
            id: "2.15",
            title: "2.15 Contact with Panel Members",
            content:
              "FEMA and the Community(s) will not contact any SRP Cadre members, potential short-listed Panelist, or Panel members about any aspect of the appeal or comment under review, before, during or after a final decision has been made.",
          },
          {
            id: "2.16",
            title: "2.16 Confidentiality",
            content: `Panelist will not disclose the internal deliberations of the Panel to FEMA, the community or to the public.

Panelist will not disclose the final decision of the Panel unless and until that decision has been made public by NIBS or the FEMA Administrator.`,
          },
        ],
      },
      {
        id: "part-iii",
        title: "PART III - REVIEW PROCESS",
        sections: [
          {
            id: "3.1",
            title: "3.1 Preliminary Review of Appeal or Comment",
            content: `NIBS will schedule as soon as practicable a preliminary meeting with the Panel members to review the appeal or comment submissions. During the preliminary review the Panel members and NIBS staff will discuss:

- Appointing a Panel Chair
- The roles and duties of Chair and members of the Panel
- The conduct of the review of the appeal or comment, including clarification of conflicting data submitted by FEMA and the community(s) and potential outcomes
- The production and distribution of documents and other information between Panel members
  - SRP portal
  - Email list
- A preliminary schedule to:
  - Formally review the data submitted by FEMA and the community
  - Conduct the community(s) and FEMA's oral presentation
  - Evaluate and deliberate on Panel findings
  - Meet to render a final decision
  - Draft, approve and deliver the final Panel report to NIBS for the FEMA Administrator
- Any other preliminary matters`,
          },
          {
            id: "3.2",
            title: "3.2 Panel Members Only Portal",
            content: `Once selected to serve, Panel members will be given access to the SRP Portal Panel page they have been appointed to. Panel members will only be allowed to access their Panel page environment.

NIBS will utilize the SRP Portal to administer and manage the individual Panels. NIBS will store, maintain, and distribute all relevant data packages, studies, documents, reports, discussion threads, actions, assignments, schedules, sections or appropriate links to the Technical and Scientific Data Notebook and individual Panel deliberations on the SRP Portal. The SRP Portal will be available to all members of an active Panel 24/7. The Panel page will include:

- A home page with the name of the individual Panel
- A short description and scope of the appeal package
- The official start and end date of the Panel
- A contact section with the name and contact information of the Panel members appointed to that Panel
- A Panel specific email tool for ease of communication with NIBS staff the SRP Chair and all the members of the Panel
- A files section to post all data, packages, analyses and perform independent reviews and exchange of information between Panel members
- A tasks section to assign and schedule specific actions to the members of a Panel
- An actions section to keep track of all assignments and expected deliverables
- A comment section to discuss specific documents with an automatic email notification feature to alert members of new postings to each discussion thread
- A calendar to track and post Panel meetings, agendas and scheduled oral presentations`,
          },
          {
            id: "3.3",
            title: "3.3 Typical SRP Process",
            content: `The typical SRP process is as follows:

- Initiation of SRP
- Acknowledge Receipt of FEMA Request for SRP
- Administrative Review of SRP Request
- Confirm Cadre Member Availability and Term of Service
- Distribute List of Short-listed Panelists
- Selection of Panel members
- Objection to Cadre Member on short list
- Notice of Selection to Serve on a Panel
- Contractual Engagement of Selected Panelists
- Kick off meeting of Panel
- Panel Review of Data Packages
- Oral Presentations
- Panel Deliberations & Decision
- Report Production
- Delivery of Final Report to FEMA Administrator

Periodic meetings will be scheduled with the Panel to monitor progresses.`,
          },
          {
            id: "3.4",
            title: "3.4 Review of Data Packages",
            content: `Panel members will receive from FEMA specific sections of the Technical and Scientific Data Notebook (TSDN) used to determine proposed flood elevations relevant to the appeal or comment, the contesting data submitted by the community(s) during the 90-day appeal period and a summary of the issue. Following the preliminary review meeting Panelists will have 30 days to get an in depth understanding of the FEMA and community data and documentation. During this period the Panel will:

- First, review the FEMA data that was contested for sound engineering practices and principle and compliance with NFIP mapping standards;
- Second, review the community(s) data on a point-by-point basis to determine which elements satisfy NFIP mapping standards and negate the FEMA data.`,
          },
          {
            id: "3.5",
            title: "3.5 Date and Time of Oral Presentation",
            content: `Oral presentations are optional and may take place if requested by the community, FEMA or the Panel. The community will include a justification for an oral presentation in the SRP Request Form. The Panel may request oral presentations on submitted data made by the community and FEMA when deemed necessary.

If requested and after consultation with the Chair of the Panel, Panel members, FEMA and the community, staff will set the date and time of the oral presentations. All parties will respond to scheduling requests for oral presentation dates and time in a timely manner.

NIBS will send a Notice of Oral Presentations to the parties at least 10 days in advance of the presentation date.`,
          },
          {
            id: "3.6",
            title: "3.6 Attendance",
            content:
              "Any persons having a direct interest in the submission can attend the oral presentation via web conference. All attendees will be required to introduce themselves during oral presentations.",
          },
          {
            id: "3.7",
            title: "3.7 Postponements",
            content:
              "The Chair may postpone any oral presentations, upon agreement of all parties, at the request of a party for good cause. Any oral presentation may proceed in the absence of any party who after due notice fails to present or fails to attain a postponement.",
          },
          {
            id: "3.8",
            title: "3.8 Waiver of Oral Presentation",
            content:
              "If an oral presentation is called, the community and/or FEMA may elect to waive oral presentations by notifying NIBS staff when contacted to schedule a presentation time and date, or at any time but at least five days prior to a scheduled presentation date.",
          },
          {
            id: "3.9",
            title: "3.9 Audio Recording",
            content:
              "The community(s) and FEMA oral presentations and Panel meetings will be recorded. The audio recordings will be the official record of all presentation and meeting proceedings. Audio recordings will be available during Panel deliberations.",
          },
          {
            id: "3.10",
            title: "3.10 Conduct and Duration of Oral Presentations",
            content: `At the discretion of the Panel Chair, and after consultation with the members of the Panel and NIBS, FEMA and the community(s) will have the opportunity to present via web conferencing the scientific or technical data submitted to the Panel. No new data will be submitted by either FEMA or the community to the Panel during oral presentations. The data the community(s) presents can only be the data submitted to FEMA during the 90-day appeal or comment period.

FEMA and the community will be offered equal time to submit their report and data. NIBS in consultation with the Panel Chair will determine the allowable time for each oral presentation and for the Panel Q&A.

Typical Presentation Timeline:
- Community presentation (30-60 minutes)
- FEMA presentation (30-60 minutes)
- Panel Q&A (30-60 minutes)`,
          },
          {
            id: "3.11",
            title: "3.11 Oral Presentations",
            content:
              "FEMA and the community will be asked to submit a slide deck of their presentation 5 days prior to the presentation date of the oral presentations for distribution to all parties.",
          },
          {
            id: "3.12",
            title: "3.12 Extension of Time",
            content: `NIBS and Panel members may modify any period of time by mutual agreement. NIBS may for good cause extend any period of time established by these rules, except the time for delivering the Panel's final written report and decision to FEMA.

The Chair may request for an extension of time, upon agreement of all Panel members, for good cause, by notifying NIBS staff.`,
          },
          {
            id: "3.13",
            title: "3.13 Deliberations and Decision",
            content: `Panel members will keep their deliberations and final reporting tightly focused on scientific and technical issues and the correctness of FEMA's data. After reviewing the contested FEMA data for sound engineering practices and principles and compliance with NFIP standards, the Panel will review the community data on a point by point basis to determine which elements satisfy NFIP mapping standards and negate the FEMA data. The Panel will render a written decision to "accept" or "deny" in part or in whole, the alternative flood elevations submitted by the community based on the scientific and technical information submitted by the community and FEMA. The Panel's decision will serve as the recommendation to the FEMA Administrator for FEMA's final determination of the flood elevations.

The decision to accept or deny the appeal or comment will include one of the following explanations:

- FEMA's data does not satisfy NFIP mapping standards defined in FEMA Guidelines and Standards for Flood Risk Analysis and Mapping and supporting guidance or FEMA Guidelines and Specifications for Flood Hazard Mapping Partners (for studies begun before the superseded date) (NFIP standards) and must be revisited.
- The community's data satisfies NFIP standards and wholly corrects or negates FEMA's data.
- Portions of the community's data satisfy NFIP standards and correct or negate FEMA's data.
- The community's data does not satisfy NFIP mapping standards, thus FEMA's data is not corrected, contradicted, or negated.
- The community's data does satisfy NFIP standards and is correct, but does not negate FEMA's data.

Final deliberations and vote of the Panel members will take place during a conference call. A simple majority vote of the Panel will decide to either recommend FEMA accept or deny the community's alternate flood elevations in part or in whole.`,
          },
          {
            id: "3.14",
            title: "3.14 Written Decision",
            content: `The Panel will present its decision in a written report made available to FEMA and the community. The report should include:

- The List of Panel members;
- A description of the appeal or comment;
- A list of the data submitted by the community and FEMA;
- A summary of the Panel activities;
- The recommendation to either accept or deny the community's data in part or in whole;
- The Panel rationale for its findings.`,
          },
          {
            id: "3.15",
            title: "3.15 Delivery of Report",
            content:
              "NIBS will deliver the Panel's written report and decision with rationale to FEMA and the community(s) within 90 days of the first meeting of the Panel.",
          },
          {
            id: "3.16",
            title: "3.16 Publication of Panel Decision",
            content:
              "The Panel report will be made public. The report will be posted on the SRP public website within 15 days of delivery to FEMA and the community.",
          },
          {
            id: "3.17",
            title: "3.17 Closing of Panel",
            content:
              "Upon delivery of the Panel report to FEMA and FEMA's acknowledgment that the Panel report is complete, NIBS will declare the Panel closed.",
          },
        ],
      },
      {
        id: "part-iv",
        title: "PART IV - LOGISTICS",
        sections: [
          {
            id: "4.1",
            title: "4.1 Panel Quorum",
            content:
              "The community's flood elevation data package will be accepted or denied in part or in whole by a simple majority of the SRP members.",
          },
          {
            id: "4.2",
            title: "4.2 Meeting Quorum",
            content:
              "The quorum for any meeting of an SRP during which any decision or vote is taken will be a full majority of all members on the Panel.",
          },
          {
            id: "4.3",
            title: "4.3 Notices",
            content:
              "Any requirement for notice as specified by these rules will be deemed satisfactory if made by e-mail to all parties to whom notice is due, or by some other electronic means that is available to all persons to whom notice is due, and the recipient's acknowledgement thereof.",
          },
          {
            id: "4.4",
            title: "4.4 Postings",
            content:
              "Any requirement for making documents or information available as specified within these rules will be deemed satisfactory if such documents are posted to a web site or portal accessible to all persons entitled to such access and accompanied by proper notice of the postings to all such persons, or by some other electronic means that is available to all persons entitled to such access.",
          },
          {
            id: "4.5",
            title: "4.5 Records",
            content:
              "Panels will keep accurate records and will work with NIBS staff as appropriate in the fulfillment or their responsibilities. The Chair who will be appointed by the Panel members or as provided by these rules will be responsible for fulfilling this obligation.",
          },
          {
            id: "4.6",
            title: "4.6 Compensation",
            content:
              "Panel members will be compensated for their services under contract with NIBS. Panel members called to serve on a panel will be compensated for each 8-hour day served on an SRP, each panel averaging two to five days' effort over the course of 90 calendar days. Chairs will be compensated for their extra services on the Panel.",
          },
        ],
      },
      {
        id: "part-v",
        title: "PART V - FORMS & NOTICES",
        sections: [
          {
            id: "5.1",
            title: "Forms & Notices",
            content: `FEMA Flood Mapping Timelines (see Appendix A)
SRP Request Form (see Appendix B)
SRP Community Submittal Agreement (see Appendix C)
FEMA Acceptable Models List (see Appendix D)`,
          },
        ],
      },
      {
        id: "part-vi",
        title: "PART VI - DEFINITIONS",
        sections: [
          {
            id: "6.1",
            title: "Definitions",
            content: `Appeal -- The submittal of knowledge or information, satisfying the data requirements set forth in 44 CFR Section 67.6, that indicates the elevations proposed by FEMA are scientifically or technically incorrect

Cadre -- Independent scientific and technical body of experts that may be asked to serve on a Scientific Resolution Panel

CEO -- Chief Executive Officer or the official of the community who is charged with the authority to implement and administer laws, ordinances, and regulations for that community.

Community -- the political entity that has the authority to adopt and enforce floodplain ordinances for the area under its jurisdiction and tribal entities as defined in 44 Code of Federal Regulations (CFR) Section 59.1

FEDD -- Flood Elevation Determination Docket

FEMA -- Federal Emergency Management Agency

FIRM -- Flood Insurance Rate Maps
LFD -- Letter of Final Determination issued by FEMA Administrator

NFIP -- National Flood Insurance Program

Panelist -- Subject matter expert, identified by NIBS as a Cadre member, selected to serve on an SRP

Sponsor -- An organization independent of and contracted by FEMA to manage and administer the SRP process

SRP -- Scientific Resolution Panel

TSDN -- Technical and Scientific Data Notebook`,
          },
        ],
      },
    ],
    []
  );

  const [selectedSection, setSelectedSection] = useState(null);

  const closeModal = () => setSelectedSection(null);

  const renderContent = (content) => {
    const lines = content.split("\n");
    const elements = [];
    let currentParagraph = [];

    lines.forEach((line, idx) => {
      const trimmedLine = line.trim();

      if (trimmedLine.startsWith("•")) {
        if (currentParagraph.length > 0) {
          elements.push(
            <p key={`p-${idx}`} className="mb-4 text-gray-700 leading-relaxed">
              {currentParagraph.join(" ")}
            </p>
          );
          currentParagraph = [];
        }
        elements.push(
          <div key={`bullet-${idx}`} className="flex gap-3 mb-3 ml-4">
            <span className="text-blue-600 font-bold mt-1">•</span>
            <span className="text-gray-700 leading-relaxed flex-1">
              {trimmedLine.substring(1).trim()}
            </span>
          </div>
        );
      } else if (trimmedLine.startsWith("-")) {
        if (currentParagraph.length > 0) {
          elements.push(
            <p key={`p-${idx}`} className="mb-4 text-gray-700 leading-relaxed">
              {currentParagraph.join(" ")}
            </p>
          );
          currentParagraph = [];
        }
        elements.push(
          <div key={`dash-${idx}`} className="flex gap-3 mb-2 ml-8">
            <span className="text-gray-500 font-bold mt-1">-</span>
            <span className="text-gray-700 leading-relaxed flex-1">
              {trimmedLine.substring(1).trim()}
            </span>
          </div>
        );
      } else if (trimmedLine === "") {
        if (currentParagraph.length > 0) {
          elements.push(
            <p key={`p-${idx}`} className="mb-4 text-gray-700 leading-relaxed">
              {currentParagraph.join(" ")}
            </p>
          );
          currentParagraph = [];
        }
      } else {
        currentParagraph.push(trimmedLine);
      }
    });

    if (currentParagraph.length > 0) {
      elements.push(
        <p key="p-last" className="mb-4 text-gray-700 leading-relaxed">
          {currentParagraph.join(" ")}
        </p>
      );
    }

    return elements;
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
        {`${PROJECT_CONFIG.name} Rules and Procedures`}
        </h1>
        <p className="text-gray-600">
          {`${PROJECT_CONFIG.name} Guidelines and Documentation`}
        </p>
      </div>

      <div className="space-y-4">
        {parts.map((part) => (
          <AccordionPart
            key={part.id}
            part={part}
            onSectionClick={(section) => setSelectedSection(section)}
          />
        ))}
      </div>

      <Modal
        isOpen={!!selectedSection}
        onClose={closeModal}
        title={selectedSection?.title || ""}
        size="xl"
      >
        <div className="space-y-4 text-gray-700 leading-relaxed max-h-[70vh] overflow-y-auto pr-2">
          {selectedSection && renderContent(selectedSection.content)}
        </div>
      </Modal>
    </div>
  );
};

export default RulesProcedures;
